import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { BasicButton, PrimaryButton } from '../../../../../../widgets/buttons';
import { Checkbox } from '../../../../../../widgets/checkbox';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

export const toggleSetItem = (set: Set<string>, id: string, selected: boolean) => {
    const next = new Set(set);
    if (selected) {
        next.add(id);
    } else {
        next.delete(id);
    }
    return next;
};

export const SelectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 20px;

    > p {
        margin: 0;
    }
`;

export const SelectionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: start;
    gap: 8px;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const selectionSurface = css<{ $selected: boolean }>`
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.component.checkbox.selected.active.border : props.theme.app.borderColor)};
    border-radius: 4px;
    background: ${(props) => (props.$selected ? props.theme.app.component.tabs.active.backgroundColor : props.theme.app.pageBackgroundColor)};
    transition:
        background 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        border-color: ${(props) =>
            props.$selected ? props.theme.app.component.checkbox.selected.active.border : props.theme.app.component.checkbox.unselected.active.border};
    }
`;

export const SelectionItemName = styled.span<{ $bold?: boolean }>`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${(props) => (props.$bold ? 600 : 400)};
    color: ${(props) => props.theme.app.textColor};
`;

const SelectionTile = styled.div<{ $selected: boolean }>`
    ${selectionSurface}
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    padding: 8px 6px 8px 16px;
    cursor: pointer;
`;

export const SelectionFooter = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-end;
    gap: 20px;
    padding-top: 20px;
`;

export const SelectionFooterPrimaryButton = styled(PrimaryButton)`
    min-width: 150px;
`;

export const SelectionFooterBasicButton = styled(BasicButton)`
    min-width: 150px;
`;

interface ComposerAttributeSelectionGridProps {
    attributes: ComposerAttributeNode[];
    selectedIds: Set<string>;
    onChange: (attributeID: string, selected: boolean) => void;
}

export const ComposerAttributeSelectionGrid: FC<ComposerAttributeSelectionGridProps> = ({ attributes, selectedIds, onChange }) => (
    <SelectionGrid>
        {attributes.map((attr) => {
            const selected = selectedIds.has(attr.id);
            return (
                <SelectionTile key={attr.id} $selected={selected} onClick={() => onChange(attr.id, !selected)}>
                    <SelectionItemName $bold title={attr.name}>
                        {attr.name}
                    </SelectionItemName>
                    <Checkbox checked={selected} onChange={(checked) => onChange(attr.id, checked)} />
                </SelectionTile>
            );
        })}
    </SelectionGrid>
);
